import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Search, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { download } from "@/components/phones/PhoneRegister";

export const AssignImportDialog = ({ open, date, stationId, doubleCrew, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (confirm) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("date", date);
    fd.append("station_id", stationId || "");
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post("/assignments/import", fd);
      if (confirm) { toast.success(`Caricate ${data.count} assegnazioni`); onDone(); } else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="assign-import-dialog">
        <DialogHeader>
          <DialogTitle>Carica assegnazioni del {new Date(date).toLocaleDateString("it-IT")}</DialogTitle>
          <DialogDescription>Colonne usate: DRIVER (o DA) e TARGA (se la colonna della targa non ha titolo, si usa quella accanto al driver). Facoltativa: TELEPASS.{doubleCrew && " Doppio equipaggio: TARGA, DA 1 e DA 2 (secondo autista)."} Le altre colonne vengono ignorate; le righe senza targa valida (vuota, 0, #N/D) vengono saltate.</DialogDescription>
        </DialogHeader>
        <Button variant="outline" onClick={() => download("/assignments/import-template", { date, station_id: stationId }, `modello_assegnazioni_${date}.xlsx`)} data-testid="assign-import-template-button">
          <Download className="mr-2 h-4 w-4" /> Scarica il file da compilare ({doubleCrew ? "TARGA, DA 1, DA 2, TELEPASS" : "DA, TARGA, TELEPASS"})
        </Button>
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="truncate">{file ? file.name : "Scegli file Excel (.xlsx)..."}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="assign-import-file-input" />
        </label>
        <Button onClick={() => send(false)} disabled={!file || busy} data-testid="assign-import-preview-button"><Search className="mr-2 h-4 w-4" /> Analizza file</Button>
        {preview && (
          <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="assign-import-preview">
            {preview.file_date && preview.file_date !== date && <p className="rounded-md bg-amber-50 p-2 text-xs font-semibold text-amber-800" data-testid="assign-import-date-warning">Attenzione: il file è del {new Date(preview.file_date).toLocaleDateString("it-IT")}, ma stai caricando nel giorno {new Date(date).toLocaleDateString("it-IT")}.</p>}
            <p><b data-testid="assign-import-count">{preview.count}</b> assegnazioni valide ({preview.new} nuove, {preview.update} da aggiornare) · {preview.new_drivers} nuovi driver{preview.double > 0 && ` · ${preview.double} con doppio equipaggio`}</p>
            {[["warnings", "avvisi (righe caricate comunque)", "bg-amber-50 text-amber-800", "warning"], ["errors", "righe scartate", "bg-rose-50 text-rose-700", "error"]].map(([k, t, cls, f]) => preview[k].length > 0 && (
              <div key={k} className={`max-h-40 overflow-auto rounded-md p-2 text-xs ${cls}`} data-testid={`assign-import-${k}`}>
                <p className="mb-1 font-semibold">{preview[k].length} {t}:</p>
                {preview[k].map((w, i) => <p key={i}>Riga {w.row} ({w.driver}): {w[f]}</p>)}
              </div>
            ))}
            <Button onClick={() => send(true)} disabled={busy || !preview.count} data-testid="assign-import-confirm-button"><Upload className="mr-2 h-4 w-4" /> Carica</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
